import dayjs from 'dayjs';
import React, {useState} from 'react';
import {v4 as uuid} from 'uuid';
import {Box, CircularProgress, Stack} from '@mui/material';
import {
  Body,
  designSystemToken,
  Icon,
  Label,
  Tooltip,
} from '@lightricks/react-design-system';
import ThumbnailDisplay from '@contentlab/components/thumbnail-display';
import {ROUTES} from '@contentlab/config/routesConstants';
import useUiInteractionEvent from '@contentlab/hooks/analytics/use-interaction-event';
import useProcessEvent from '@contentlab/hooks/analytics/use-process-event';
import {contentLabActions} from '@contentlab/stores/contentLabStore';
import {Project, ProjectStatus} from '@contentlab/types/project';
import {ProjectStatusUpdate} from '@contentlab/types/projectStatusUpdate';
import {CONTENT_LAB_VIEW_NAMES} from '@/lib/analytics/analyticsConstants';
import translate from '@/utils/translate';
import {TIME_HOUR_MINUTE_WITH_AM_PM_FORMAT} from '@/config/timeFormats';
import Conditional from '@/components/conditional';
import Divider from '@/components/divider';
import useNavigation from '@/hooks/use-navigation/useNavigation';
import downloadProject from '../../utils/downloadProject';

interface ProjectRowProps {
  project: Project;
  presentationId: string;
  projectStatus: ProjectStatusUpdate;
}

interface ProjectProgressProps {
  project: Project;
  projectStatus: ProjectStatusUpdate;
}

function ProjectProgress(props: ProjectProgressProps) {
  const {project, projectStatus} = props;
  const time = dayjs(project.createdAt).format(
    TIME_HOUR_MINUTE_WITH_AM_PM_FORMAT
  );

  const getFailedCount = (failedCount: number) => {
    return (
      <Conditional condition={failedCount > 0}>
        <Box display="flex" flexDirection="row" gap="4px">
          <Body size="md" color={designSystemToken('semantic.fg.secondary')}>
            ,
          </Body>
          <Body size="md" color={designSystemToken('semantic.fg.danger')}>
            {translate('failed count', {count: failedCount})}
          </Body>
        </Box>
      </Conditional>
    );
  };
  const getCompletedProjectStatus = () => {
    const {totalFlows, failedCount} = projectStatus.progressInfo;

    return (
      <Box display="flex" flexDirection="row">
        <Body size="md" color={designSystemToken('semantic.fg.secondary')}>
          {translate('file count', {count: totalFlows})}
        </Body>
        {getFailedCount(failedCount)}
      </Box>
    );
  };
  const getInProgressProjectStatus = () => {
    const {totalFlows, readyCount, failedCount} = projectStatus.progressInfo;

    return (
      <Box display="flex" flexDirection="row">
        <Body size="md" color={designSystemToken('semantic.fg.secondary')}>
          {translate('files completed', {
            completed: readyCount,
            count: totalFlows,
          })}
        </Body>
        {getFailedCount(failedCount)}
      </Box>
    );
  };

  const progress =
    projectStatus.status === ProjectStatus.READY
      ? getCompletedProjectStatus()
      : getInProgressProjectStatus();

  return (
    <Box display="flex" flexDirection="row" alignItems="center" gap="8px">
      <Body size="md" color={designSystemToken('semantic.fg.secondary')}>
        {time}
      </Body>
      <Divider
        vertical
        margin={6}
        width="0.5px"
        height="13px"
        color={designSystemToken('semantic.fg.tertiary')}
        style={{marginBottom: '1px'}}
      />
      {progress}
    </Box>
  );
}

function ProjectRow({project, presentationId, projectStatus}: ProjectRowProps) {
  const {navigate} = useNavigation();
  const [downloadInProgress, setDownloadInProgress] = useState<boolean>(false);
  const {uiInteraction} = useUiInteractionEvent();
  const {started, ended} = useProcessEvent();
  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      paddingY="12px"
      sx={{cursor: 'default'}}
    >
      <ThumbnailDisplay
        width={40}
        height={40}
        isLoading={projectStatus.status === ProjectStatus.IN_PROGRESS}
        loadingType="shimmer"
        thumbnail={projectStatus.thumbnailUrl}
      />
      <Stack
        direction="column"
        flex={1}
        paddingX="16px"
        justifyContent="center"
      >
        <Label size="lg">{project.name}</Label>
        <ProjectProgress project={project} projectStatus={projectStatus} />
      </Stack>
      {projectStatus.status === ProjectStatus.READY ? (
        <Tooltip
          title={
            <Body size="md" color={designSystemToken('semantic.fg.primary')}>
              {translate('Download project')}
            </Body>
          }
          placement="top-end"
          slotProps={{
            popper: {
              modifiers: [{name: 'offset', options: {offset: [0, -10]}}],
            },
          }}
          arrow
        >
          <Box
            padding="8px"
            sx={{
              cursor: `${
                projectStatus.status === ProjectStatus.READY
                  ? 'pointer'
                  : 'default'
              }`,
            }}
            onClick={async () => {
              if (downloadInProgress) {
                return;
              }
              const interactionId = uuid();
              uiInteraction({
                item_name: 'download_project',
                presentation_id: presentationId,
                interaction_location: CONTENT_LAB_VIEW_NAMES.PROJECTS_SHELF,
                interaction_id: interactionId,
                asset_id: project.id,
              });
              if (projectStatus.status !== ProjectStatus.READY) {
                return;
              }
              try {
                setDownloadInProgress(true);
                await downloadProject(
                  project.id,
                  interactionId,
                  CONTENT_LAB_VIEW_NAMES.PROJECTS_SHELF,
                  {started, ended}
                );
              } catch {
                /* empty */
              } finally {
                setDownloadInProgress(false);
              }
            }}
          >
            {downloadInProgress ? (
              <CircularProgress sx={{color: 'black'}} size={24} />
            ) : (
              <Icon
                name="Navigation-Download"
                appearance={
                  projectStatus.status === ProjectStatus.READY
                    ? 'primary'
                    : 'disabled'
                }
                size="medium"
              />
            )}
          </Box>
        </Tooltip>
      ) : null}
      <Tooltip
        title={
          <Body size="md" color={designSystemToken('semantic.fg.primary')}>
            {translate('Open project')}
          </Body>
        }
        placement="top-end"
        slotProps={{
          popper: {
            modifiers: [{name: 'offset', options: {offset: [0, -10]}}],
          },
        }}
        disableHoverListener={projectStatus.progressInfo.readyCount === 0}
        arrow
      >
        <Box
          padding="8px"
          sx={{
            cursor:
              projectStatus.progressInfo.readyCount === 0
                ? 'default'
                : 'pointer',
          }}
          onClick={() => {
            if (projectStatus.progressInfo.readyCount === 0) {
              return;
            }
            contentLabActions.setProjectFilters(project.name);
            navigate(
              `${ROUTES.CONTENT_LAB}${ROUTES.CONTENT_LAB_PROJECT}/${project.id}`
            );
          }}
        >
          <Icon
            name="Navigation-Projects"
            appearance={
              projectStatus.progressInfo.readyCount === 0
                ? 'disabled'
                : 'primary'
            }
            size="medium"
          />
        </Box>
      </Tooltip>
    </Stack>
  );
}

export default ProjectRow;
